import React, { useEffect, forwardRef, useRef, useCallback, useLayoutEffect } from 'react'
import Phaser from 'phaser'
import usePhaser from '../hooks/usePhaser'

const GameObject = forwardRef(({
  args,
  type = 'Shape',
  addedToScene = () => {},
  addedToSceneForParent = () => {},
  children,
  dataParentRef,
  ...props
}, externalRef) => {
  const { scene } = usePhaser()
  const localRef = useRef()
  const tId = useRef()
  const ref = externalRef || localRef
  
  const applyProps = useCallback((_this, p) => {
    Object.keys(p).forEach(k => {
      if(_this?.hasOwnProperty(k) || _this?.__proto__.hasOwnProperty(k)) {
        if(typeof _this[k] === 'function') {
          _this[k](p[k])
        } else {
          _this[k] = p[k]
        }
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props])
  
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => applyProps(ref.current, props), [props])
  
  // IMPORTANT: keep this as useEffect so this code runs after render
  // that way we have all Phaser listeners settled once the objects are created
  useEffect(() => {
    if (scene) {
      // new object
      const obj = new Phaser.GameObjects[type](scene, ...args)
      
      // makes Rect aware of the new object
      ref.current = obj
      
      // listen to added-to-scne event
      obj.once(Phaser.GameObjects.Events.ADDED_TO_SCENE, () => {
        // convenient callback for event as object property
        addedToScene(obj)
        
        // calls parent container callback (if present)
        addedToSceneForParent(obj)

        // update Phaser with React props
        applyProps(obj, props)
      })

      // offset execution to let object creation propagate on refs before being added to de scene
      tId.current = setTimeout(() => {
        // adds object to scene
        scene.add.existing(obj)
      })
      
    }

    return () => {
      ref?.current?.destroy()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scene])

  useEffect(() =>{
    return () => {
      clearTimeout(tId.current)
  }}, [])

  return <>{children}</>
})
GameObject.displayName = `GameObject`;
export default GameObject
